import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowUpRight } from "lucide-react";
import { PAVILIONS, MAHATALKS_TOPICS, MASTERCLASSES, AWARDS, ZONES, FAQS, PROGRAMME } from "@/data/content";

const PAGES = [
    { title: "Home", type: "Page", to: "/" },
    { title: "About MahaBazaar", type: "Page", to: "/about" },
    { title: "The Experience", type: "Page", to: "/experience" },
    { title: "Programme & Agenda", type: "Page", to: "/programme" },
    { title: "PitchYatra 2027", type: "Page", to: "/pitchyatra" },
    { title: "Exhibition", type: "Page", to: "/exhibition" },
    { title: "Investor Adda", type: "Page", to: "/investor-adda" },
    { title: "Knowledge Labs", type: "Page", to: "/knowledge-labs" },
    { title: "Awards 2027", type: "Page", to: "/awards" },
    { title: "Special Zones", type: "Page", to: "/special-zones" },
    { title: "Registration", type: "Page", to: "/register" },
    { title: "Partner With Us", type: "Page", to: "/partner" },
    { title: "Venue & Event Map", type: "Page", to: "/venue" },
    { title: "MahaBazaar Insights", type: "Page", to: "/insights" },
    { title: "FAQ", type: "Page", to: "/faq" },
    { title: "Contact", type: "Page", to: "/contact" },
];

export default function SearchModal({ open, onClose }) {
    const [q, setQ] = useState("");
    const navigate = useNavigate();

    const index = useMemo(
        () => [
            ...PAGES,
            ...PAVILIONS.map((p) => ({ title: p.name, type: "Pavilion", to: "/experience#pavilions" })),
            ...MAHATALKS_TOPICS.map((t) => ({ title: t, type: "MahaTalks", to: "/experience#mahatalks" })),
            ...MASTERCLASSES.map((m) => ({ title: m.title, type: "Masterclass", to: "/knowledge-labs" })),
            ...AWARDS.map((a) => ({ title: a.name, type: "Award", to: "/awards" })),
            ...ZONES.map((z) => ({ title: z.name, type: "Zone", to: "/special-zones" })),
            ...PROGRAMME.map((s) => ({ title: s.title, type: `Day ${s.day} · ${s.type}`, to: "/programme" })),
            ...FAQS.map((f) => ({ title: f.q, type: "FAQ", to: "/faq" })),
        ],
        [],
    );

    const results = useMemo(() => {
        const query = q.trim().toLowerCase();
        if (!query) return PAGES;
        return index.filter((i) => i.title.toLowerCase().includes(query)).slice(0, 14);
    }, [q, index]);

    useEffect(() => {
        if (!open) return;
        setQ("");
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;

    const go = (to) => {
        onClose();
        navigate(to);
    };

    return (
        <div className="fixed inset-0 z-[110] flex items-start justify-center pt-24 px-4" role="dialog" aria-modal="true" data-testid="search-modal">
            <div className="absolute inset-0 bg-[#100206]/90 backdrop-blur-md" onClick={onClose} />
            <div className="relative card-luxe w-full max-w-2xl shadow-2xl">
                <div className="flex items-center gap-3 border-b hairline px-5 py-4">
                    <Search size={17} strokeWidth={1.5} className="text-[var(--gold)] shrink-0" />
                    <input
                        autoFocus
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search pavilions, sessions, awards, pages…"
                        data-testid="search-input"
                        className="w-full bg-transparent text-base text-[var(--ivory)] placeholder:text-[var(--muted)]/50 focus:outline-none"
                    />
                    <button onClick={onClose} aria-label="Close search" data-testid="search-close-btn"
                        className="text-[var(--muted)] hover:text-[var(--champagne)] transition-colors">
                        <X size={17} strokeWidth={1.5} />
                    </button>
                </div>
                <div className="max-h-[55vh] overflow-y-auto p-2" data-testid="search-results">
                    {results.length === 0 && (
                        <p className="px-4 py-8 text-sm text-muted-luxe text-center">No results found in the bazaar.</p>
                    )}
                    {results.map((r, i) => (
                        <button key={`${r.title}-${i}`} onClick={() => go(r.to)} data-testid={`search-result-${i}`}
                            className="w-full flex items-center justify-between gap-4 px-4 py-3.5 text-left hover:bg-[rgba(201,162,75,0.06)] transition-colors group">
                            <span>
                                <span className="block text-[0.56rem] tracking-[0.3em] uppercase text-[var(--gold)]/80">{r.type}</span>
                                <span className="block text-sm text-[var(--ivory)]/85 mt-0.5">{r.title}</span>
                            </span>
                            <ArrowUpRight size={14} strokeWidth={1.5} className="text-[var(--gold)] opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
